import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import WeatherLoader from "../../components/loader";
import store from "../../store";
import "./styles/_index.scss";
import axios from "axios";

type WeatherParameter = {
  name: string;
  leftParameter?: string;
  leftComparator?: string;
  leftLimit?: string;
  leftMarginal?: string;
  operator?: string;
  rightParameter?: string;
  rightComparator?: string;
  rightLimit?: string;
  rightMarginal?: string;
  hasInputs: boolean;
  isActive: boolean;
  criteriaId?: number;
};

type CriteriaDetail = {
  forecast_osf_criteria_id: number;
  field_id: number;
  field_name: string;
  margin_value: string;
  value: string;
  comparison_operator_id: number;
  parameter_display_name?: string;
  logical_operator?: string;
};

type CriteriaData = {
  forecast_osf_criteria_id: number;
  forecast_id: number;
  criteria_name: string;
};

//type DataRecord = Record<string, number | null | undefined>;

function tableHeaderFn(caption: any) {
  var text;
  switch (caption) {
    case "a_10mwinddir":
      text = "10m Wind Dir";
      break;
    case "a_10mwindspeed":
      text = "10m Wind Speed";
      break;
    case "a_10mgust":
      text = "10m Wind Gust";
      break;
    case "a_50mwindspeed":
      text = "50m Wind Speed";
      break;
    case "a_100mwindspeed":
      text = "100m Wind Speed";
      break;
    case "windseaheight":
      text = "Wind Sea Height";
      break;
    case "windseadirection":
      text = "Wind Sea Dir";
      break;
    case "totalprecip":
      text = "Total Precip";
      break;
    case "swell1direction":
      text = "Swell1 Dir";
      break;
    case "swell2direction":
      text = "Swell2 Dir";
      break;
    case "swell1height":
      text = "Swell1 Height";
      break;
    case "swell2height":
      text = "Swell2 Height";
      break;
    case "sigwaveheight":
      text = "Sig Wave Height";
      break;
    case "peakwaveperiod":
      text = "Peak Wave Period";
      break;
    case "maxwave":
      text = "Max Wave";
      break;
    case "surfacecurrentdirection":
      text = "Surface Current Dir";
      break;
    case "surfacecurrentspeed":
      text = "Surface Current Speed";
      break;
    case "modelvisibility":
      text = "Visibility";
      break;
    case "windseaperiod":
      text = "Wind Sea Period";
      break;
    case "swell1period":
      text = "Swell1 Period";
      break;
    case "a_2mtemp":
      text = "2m Temp";
      break;
    case "mslp":
      text = "MSLP";
      break;
    case "cloudbase":
      text = "Cloud Base";
      break;
    case "rainrate":
      text = "Rain Rate";
      break;
    case "peakwavedir":
      text = "Peak Wave Dir";
      break;
    case "swell2period":
      text = "Swell2 Period";
      break;
    default:
      text = caption;
  }
  return text;
}

interface WeatherParameterSettingsProps {
  criteriaDatas: CriteriaData[];
  criteriaDetailDatas: CriteriaDetail[];
  originalCriteriaDetailDatas: CriteriaDetail[];
  onParameterChange: (updatedCriteriaDetails: CriteriaDetail[]) => void;
  onReset: () => void;
  tableScrollRef?: React.RefObject<HTMLDivElement>;
  showScrollIndicators?: { left: boolean; right: boolean };
}

const WeatherParameterSettings: React.FC<WeatherParameterSettingsProps> = ({
  criteriaDatas,
  criteriaDetailDatas,
  originalCriteriaDetailDatas,
  onParameterChange,
  onReset,
  tableScrollRef,
  showScrollIndicators = { left: false, right: false },
}) => {
  const [parameters, setParameters] = useState<WeatherParameter[]>([]);

  const [originalParameters, setOriginalParameters] = useState<
    WeatherParameter[]
  >([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [operatorMap, setOperatorMap] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [criteriaFields, setCriteriaFields] = useState<
    Record<number, CriteriaDetail[]>
  >({});

  const extractLogicalOperator = (details: CriteriaDetail[]): string => {
    if (details.length > 0) {
      if (details[0].logical_operator) {
        return details[0].logical_operator;
      }

      if (details[0].comparison_operator_id === 1) {
        return "AND";
      } else if (details[0].comparison_operator_id === 2) {
        return "OR";
      }

      const operatorFromValue = details.find(
        (d) =>
          typeof d.value === "string" &&
          (d.value.startsWith("OP_AND:") || d.value.startsWith("OP_OR:"))
      );

      if (operatorFromValue && typeof operatorFromValue.value === "string") {
        if (operatorFromValue.value.startsWith("OP_AND:")) return "AND";
        if (operatorFromValue.value.startsWith("OP_OR:")) return "OR";
      }
    }

    return "AND";
  };

  useEffect(() => {
    if (criteriaDatas.length > 0 && criteriaDetailDatas.length > 0) {
      const fieldsMap: Record<number, CriteriaDetail[]> = {};

      criteriaDatas.forEach((criteria) => {
        const criteriaId = criteria.forecast_osf_criteria_id;
        const fields = criteriaDetailDatas.filter(
          (detail) => detail.forecast_osf_criteria_id === criteriaId
        );

        const sortedFields = [...fields].sort((a, b) => {
          if (a.field_id === 2) return -1;
          if (b.field_id === 2) return 1;
          return a.field_id - b.field_id;
        });

        fieldsMap[criteriaId] = sortedFields;
      });

      setCriteriaFields(fieldsMap);

      const initialParameters: WeatherParameter[] = criteriaDatas.map(
        (criteria) => {
          const criteriaId = criteria.forecast_osf_criteria_id;
          const detailsForCriteria = criteriaDetailDatas.filter(
            (detail) => detail.forecast_osf_criteria_id === criteriaId
          );

          const existingParam = parameters.find(
            (p) => p.criteriaId === criteriaId
          );

          let operator = "AND";

          if (operatorMap[criteriaId.toString()]) {
            operator = operatorMap[criteriaId.toString()];
          } else if (existingParam?.operator) {
            operator = existingParam.operator;
          } else if (detailsForCriteria.length > 0) {
            operator = extractLogicalOperator(detailsForCriteria);
          }

          const param: WeatherParameter = {
            name: criteria.criteria_name,
            hasInputs: true,
            isActive: detailsForCriteria.length > 0,
            criteriaId: criteriaId,
            operator: operator,
            leftComparator: ">=",
            rightComparator: ">=",
          };

          const leftDetails = detailsForCriteria.filter(
            (detail) => detail.field_id && detail.field_id < 10
          );
          const rightDetails = detailsForCriteria.filter(
            (detail) => detail.field_id && detail.field_id >= 10
          );

          if (leftDetails.length > 0) {
            const leftDetail = leftDetails[0];
            param.leftParameter = leftDetail.field_name;

            let leftValue = leftDetail.value;
            if (
              typeof leftValue === "string" &&
              (leftValue.startsWith("OP_AND:") ||
                leftValue.startsWith("OP_OR:"))
            ) {
              leftValue = leftValue.replace(/^OP_(AND|OR):/, "");
            }

            param.leftLimit = existingParam?.leftLimit || leftValue || "";
            param.leftMarginal =
              existingParam?.leftMarginal || leftDetail.margin_value || "";

            param.leftComparator =
              existingParam?.leftComparator ||
              getComparisonSymbol(leftDetail.comparison_operator_id) ||
              ">=";
          }

          if (rightDetails.length > 0) {
            const rightDetail = rightDetails[0];
            param.rightParameter = rightDetail.field_name;

            let rightValue = rightDetail.value;
            if (
              typeof rightValue === "string" &&
              (rightValue.startsWith("OP_AND:") ||
                rightValue.startsWith("OP_OR:"))
            ) {
              rightValue = rightValue.replace(/^OP_(AND|OR):/, "");
            }

            param.rightLimit = existingParam?.rightLimit || rightValue || "";
            param.rightMarginal =
              existingParam?.rightMarginal || rightDetail.margin_value || "";

            param.rightComparator =
              existingParam?.rightComparator ||
              getComparisonSymbol(rightDetail.comparison_operator_id) ||
              ">=";
          }

          return param;
        }
      );

      setParameters(initialParameters);

      if (!isInitialized) {
        setOriginalParameters(JSON.parse(JSON.stringify(initialParameters)));
        setIsInitialized(true);

        const newOperatorMap: Record<string, string> = {};
        criteriaDatas.forEach((criteria) => {
          const criteriaId = criteria.forecast_osf_criteria_id;
          const details = criteriaDetailDatas.filter(
            (detail) => detail.forecast_osf_criteria_id === criteriaId
          );

          details.forEach((detail) => {
            const fieldKey = `${criteriaId}_${detail.field_id}`;
            newOperatorMap[fieldKey] =
              detail.logical_operator ||
              extractLogicalOperator([detail]) ||
              "AND";
          });
        });
        setOperatorMap(newOperatorMap);
      }
    }
  }, [
    criteriaDatas,
    criteriaDetailDatas,
    parameters,
    operatorMap,
    isInitialized,
  ]);
const getComparisonSymbol = (operatorId: number): string => {
  switch (operatorId) {
    case 1:
      return "<";
    case 2:
      return ">";
    case 3:
      return "<=";
    case 4:
      return ">=";
    default:
      return ">=";
  }
};

  const getComparisonOperatorId = (symbol: string): number => {
    if (symbol === "<=") {
      return 3;
    } else if (symbol === ">=") {
      return 4;
    } else {
      return 4;
    }
  };

  const getLogicalOperatorId = (operator: string): number => {
    return operator === "AND" ? 1 : 2;
  };

  const getUnitForParameter = (caption: string): string => {
    switch (caption) {
      case "10m Wind Dir":
        return " [°]";
      case "10m Wind Speed":
        return " [kts]";
      case "10m Wind Gust":
        return " [kts]";
      case "50m Wind Speed":
        return " [kts]";
      case "100m Wind Speed":
        return " [kts]";
      case "2m Temp":
        return " [°C]";
      case "Rain Rate":
        return " [mm/hr]";
      case "MSLP":
        return " [mbar]";
      case "Cloud Base":
        return " [m]";
      case "Wind Sea Height":
      case "Swell1 Height":
      case "Swell2 Height":
      case "Sig Wave Height":
      case "Max Wave":
        return " [m]";
      case "Wind Sea Period":
      case "Swell1 Period":
      case "Swell2 Period":
      case "Peak Wave Period":
        return " [s]";
      case "Peak Wave Dir":
      case "Wind Sea Dir":
      case "Swell1 Dir":
      case "Swell2 Dir":
      case "Surface Current Dir":
        return " [°]";
      case "Visibility":
        return " [km]";
      case "Total Precip":
        return " [mm]";
      case "Surface Current Speed":
        return " [m/s]";
      default:
        return "";
    }
  };

  const handleParameterChange = (
    criteriaId: number,
    fieldId: number,
    field: string,
    value: string
  ) => {
    const updatedCriteriaDetails: CriteriaDetail[] = JSON.parse(
      JSON.stringify(criteriaDetailDatas)
    );

    const detailIndex = updatedCriteriaDetails.findIndex(
      (detail) =>
        detail.forecast_osf_criteria_id === criteriaId &&
        detail.field_id === fieldId
    );

    if (detailIndex >= 0) {
      const detail = updatedCriteriaDetails[detailIndex];

      if (field === "Limit") {
        // Update the limit value
        if (
          typeof detail.value === "string" &&
          (detail.value.startsWith("OP_AND:") ||
            detail.value.startsWith("OP_OR:"))
        ) {
          const prefixMatch = detail.value.match(/^OP_(AND|OR):/);
          const prefix = prefixMatch ? prefixMatch[0] : "OP_AND:";
          detail.value = `${prefix}${value}`;
        } else {
          detail.value = value;
        }

        // Automatically update marginal to 80% of limit value
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue > 0) {
          const marginalValue = (numericValue * 0.8).toFixed(2);
          detail.margin_value = marginalValue;
        }
      } else if (field === "Marginal") {
        const numericMarginalValue = parseFloat(value);
        const currentLimitValue =
          typeof detail.value === "string" &&
          (detail.value.startsWith("OP_AND:") ||
            detail.value.startsWith("OP_OR:"))
            ? parseFloat(detail.value.replace(/^OP_(AND|OR):/, ""))
            : parseFloat(detail.value);

        if (
          !isNaN(numericMarginalValue) &&
          !isNaN(currentLimitValue) &&
          numericMarginalValue >= currentLimitValue
        ) {
          setDialogMessage(
            `Marginal value (${numericMarginalValue}) cannot exceed or equal the limit value (${currentLimitValue}). Please enter a value less than ${currentLimitValue}.`
          );
          setDialogOpen(true);
          return;
        }
        detail.margin_value = value;
      } else if (field === "Comparator") {
        detail.comparison_operator_id = getComparisonOperatorId(value);
      } else if (field === "LogicalOperator") {
        detail.logical_operator = value;
        detail.comparison_operator_id = getLogicalOperatorId(value);

        if (typeof detail.value === "string") {
          if (
            !detail.value.startsWith("OP_AND:") &&
            !detail.value.startsWith("OP_OR:")
          ) {
            detail.value = `OP_${value}:${detail.value}`;
          } else {
            detail.value = detail.value.replace(
              /^OP_(AND|OR):/,
              `OP_${value}:`
            );
          }
        }

        const fieldKey = `${criteriaId}_${fieldId}`;
        const newOperatorMap = { ...operatorMap };
        newOperatorMap[fieldKey] = value;
        setOperatorMap(newOperatorMap);
      }

      onParameterChange(updatedCriteriaDetails);
    }
  };

  const handleReset = () => {
    const resetParameters: WeatherParameter[] = JSON.parse(
      JSON.stringify(originalParameters)
    );
    setParameters(resetParameters);
    const newOperatorMap: Record<string, string> = {};
    criteriaDatas.forEach((criteria) => {
      const criteriaId = criteria.forecast_osf_criteria_id;
      const details = originalCriteriaDetailDatas.filter(
        (detail) => detail.forecast_osf_criteria_id === criteriaId
      );

      details.forEach((detail) => {
        const fieldKey = `${criteriaId}_${detail.field_id}`;
        newOperatorMap[fieldKey] =
          detail.logical_operator || extractLogicalOperator([detail]) || "AND";
      });
    });
    setOperatorMap(newOperatorMap);
    onParameterChange([...originalCriteriaDetailDatas]);
  };

  const formatValueForDisplay = (
    value: string | number,
    parameterName: string,
    isEditing: boolean = false
  ): string => {
    if (!value && value !== 0) return "";

    if (isEditing) {
      return value.toString();
    }

    const numValue = parseFloat(value.toString());
    if (isNaN(numValue)) return value.toString();

    // Wave height parameters - 1 decimal place
    if (
      parameterName.toLowerCase().includes("wave") ||
      parameterName.toLowerCase().includes("swell") ||
      parameterName.toLowerCase().includes("height")
    ) {
      return numValue.toFixed(1);
    }

    // Wind speed parameters - ALLOW decimal places, don't round
    if (
      parameterName.toLowerCase().includes("wind") &&
      parameterName.toLowerCase().includes("speed")
    ) {
      return numValue % 1 === 0 ? numValue.toString() : numValue.toString();
    }

    return value.toString();
  };

  const renderFieldControls = (
    criteria: CriteriaData,
    field: CriteriaDetail
  ) => {
    const rawValue =
      field.value && typeof field.value === "string"
        ? field.value.replace(/^OP_(AND|OR):/, "")
        : field.value;

    const fieldKey = `${criteria.forecast_osf_criteria_id}_${field.field_id}`;
    const logicalOperator =
      operatorMap[fieldKey] || field.logical_operator || "AND";

    const formattedFieldName =
      typeof tableHeaderFn === "function"
        ? tableHeaderFn(field.field_name)
        : field.field_name;

    const unit = getUnitForParameter(formattedFieldName);
    const displayName = `${formattedFieldName}${unit}`;

    const limitInputKey = `${criteria.forecast_osf_criteria_id}_${field.field_id}_limit`;
    const marginalInputKey = `${criteria.forecast_osf_criteria_id}_${field.field_id}_marginal`;

    const isEditingLimit = editingField === limitInputKey;
    const isEditingMarginal = editingField === marginalInputKey;

    const formattedValue = formatValueForDisplay(
      rawValue,
      formattedFieldName,
      isEditingLimit
    );
    const formattedMarginalValue = formatValueForDisplay(
      field.margin_value,
      formattedFieldName,
      isEditingMarginal
    );

    return (
      <>
        <td className="parameter-cell">{displayName}</td>

        <td className="comparator-cell">
          <select
            value={getComparisonSymbol(field.comparison_operator_id)}
            onChange={(e) =>
              handleParameterChange(
                criteria.forecast_osf_criteria_id,
                field.field_id,
                "Comparator",
                e.target.value
              )
            }
          >
            <option value=">=">{">="}</option>
            <option value="<=">{"=<"}</option>
          </select>
        </td>

        <td className="value-cell">
          <input
            type="text"
            value={formattedValue || ""}
            onFocus={() => setEditingField(limitInputKey)}
            onBlur={() => {
              setEditingField(null);
            }}
            onChange={(e) => {
              handleParameterChange(
                criteria.forecast_osf_criteria_id,
                field.field_id,
                "Limit",
                e.target.value
              );
            }}
          />
        </td>

        <td className="marginal-cell">
          <input
            type="text"
            value={formattedMarginalValue || ""}
            onFocus={() => setEditingField(marginalInputKey)}
            onBlur={() => {
              setEditingField(null);
            }}
            onChange={(e) => {
              handleParameterChange(
                criteria.forecast_osf_criteria_id,
                field.field_id,
                "Marginal",
                e.target.value
              );
            }}
          />
        </td>

        {field !==
          criteriaFields[criteria.forecast_osf_criteria_id]?.slice(-1)[0] && (
          <td className="operator-cell">
            <select
              value={logicalOperator}
              onChange={(e) =>
                handleParameterChange(
                  criteria.forecast_osf_criteria_id,
                  field.field_id,
                  "LogicalOperator",
                  e.target.value
                )
              }
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          </td>
        )}
      </>
    );
  };

  return (
    <div className="Modifying-Weather">
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "2px solid #132c3e",
            boxShadow: 3,
            overflow: "hidden",
            zIndex: 10000,
          },
        }}
        sx={{
          zIndex: 10000,
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            backgroundColor: "#ffff",
            borderBottom: "1px solid #132c3e",
            textAlign: "center",
            fontWeight: 600,
            color: "#132c3e",
            py: 2,
          }}
        >
          Invalid Marginal Value
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            textAlign: "center",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              lineHeight: 1.6,
              color: "#132c3e",
              fontSize: "16px",
            }}
          >
            {dialogMessage}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            backgroundColor: "#fafafa",
            borderTop: "1px solid #132c3e",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => setDialogOpen(false)}
            variant="contained"
            sx={{
              px: 4,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "#132c3e",
              "&:hover": {
                backgroundColor: "#0f2534",
              },
            }}
          >
            Understood
          </Button>
        </DialogActions>
      </Dialog>

      <div className="table-Weather" ref={tableScrollRef}>
        <table>
          <thead>
            <tr className="ModifyHeader-row">
              <th>Criteria</th>
              {(() => {
                const maxFieldsCount = Object.values(criteriaFields).reduce(
                  (max, fields) => Math.max(max, fields.length),
                  0
                );

                const headers = [];

                for (let i = 0; i < maxFieldsCount; i++) {
                  const isLastGroup = i === maxFieldsCount - 1;

                  headers.push(
                    <React.Fragment key={`header-group-${i}`}>
                      <th className="parameter-column">Parameter</th>
                      <th className="comparator-column"></th>
                      <th className="value-column">Limit</th>
                      <th className="marginal-column">Marginal</th>
                      {!isLastGroup && (
                        <th className="operator-column">Operator</th>
                      )}
                    </React.Fragment>
                  );
                }

                return headers;
              })()}
            </tr>
          </thead>
          <tbody>
            {criteriaDatas.map((criteria) => {
              const fields =
                criteriaFields[criteria.forecast_osf_criteria_id] || [];
              const isActive = fields.length > 0;

              return (
                <tr
                  key={criteria.forecast_osf_criteria_id}
                  className={`data-row ${isActive ? "active-criteria" : ""}`}
                >
                  <td className="criteria-name">{criteria.criteria_name}</td>

                  {fields.map((field, fieldIndex) =>
                    renderFieldControls(criteria, field)
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="actions-bar">
        <button className="reset-button" onClick={handleReset}>
          Reset to original
        </button>
      </div>
    </div>
  );
};

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

export default function Weather() {
  const windowWidth = useRef(window.innerWidth);

  const [open, setOpen] = useState(windowWidth.current > 1000 ? true : false);
  const data = useSelector((state: any) => state?.app);

  const [criteriaDatas, setCriteriaDatas] = useState<CriteriaData[]>([]);
  const [criteriaDetailDatas, setCriteriaDetailDatas] = useState<
    CriteriaDetail[]
  >([]);
  const [originalCriteriaDetailDatas, setOriginalCriteriaDetailDatas] =
    useState<CriteriaDetail[]>([]);
  const [TableDatas, setTableDatas] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [SelectValue, setSelectValue] = useState<string>("");
  const [dataUpdated, setDataUpdated] = useState(false);
  const [showParameterSettings, setShowParameterSettings] =
    useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [settingsPosition, setSettingsPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [showScrollIndicators, setShowScrollIndicators] = useState({
    left: false,
    right: false,
  });
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Function to get comparison symbol
  const getComparisonSymbol = (operatorId: number): string => {
    switch (operatorId) {
      case 1:
        return "<";
      case 2:
        return ">";
      case 3:
        return "<=";
      case 4:
        return ">=";
      default:
        return ">=";
    }
  };

  // Function to get unit for parameter
  const getUnitForParameter = (caption: string): string => {
    switch (caption) {
      case "10m Wind Dir":
        return " [°]";
      case "10m Wind Speed":
        return " [kts]";
      case "10m Wind Gust":
        return " [kts]";
      case "50m Wind Speed":
        return " [kts]";
      case "100m Wind Speed":
        return " [kts]";
      case "2m Temp":
        return " [°C]";
      case "Rain Rate":
        return " [mm/hr]";
      case "MSLP":
        return " [mbar]";
      case "Cloud Base":
        return " [m]";
      case "Wind Sea Height":
      case "Swell1 Height":
      case "Swell2 Height":
      case "Sig Wave Height":
      case "Max Wave":
        return " [m]";
      case "Wind Sea Period":
      case "Swell1 Period":
      case "Swell2 Period":
      case "Peak Wave Period":
        return " [s]";
      case "Peak Wave Dir":
      case "Wind Sea Dir":
      case "Swell1 Dir":
      case "Swell2 Dir":
      case "Surface Current Dir":
        return " [°]";
      case "Visibility":
        return " [km]";
      case "Total Precip":
        return " [mm]";
      case "Surface Current Speed":
        return " [m/s]";
      default:
        return "";
    }
  };

  const generateCriteriaDescription = (criteriaId: number): string => {
    const criteriaFields = criteriaDetailDatas.filter(
      (detail) => detail.forecast_osf_criteria_id === criteriaId
    );

    if (criteriaFields.length === 0) return "";

    const sortedFields = [...criteriaFields].sort(
      (a, b) => a.field_id - b.field_id
    );

    const fieldDescriptions = sortedFields.map((field, index) => {
      const displayName = tableHeaderFn(field.field_name);
      const unit = getUnitForParameter(displayName);

      let cleanValue = field.value;
      if (
        typeof cleanValue === "string" &&
        (cleanValue.startsWith("OP_AND:") || cleanValue.startsWith("OP_OR:"))
      ) {
        cleanValue = cleanValue.replace(/^OP_(AND|OR):/, "");
      }
      const originalField = originalCriteriaDetailDatas.find(
        (orig) =>
          orig.forecast_osf_criteria_id === field.forecast_osf_criteria_id &&
          orig.field_id === field.field_id
      );

      const formatValue = (
        val: string | number,
        paramName: string,
        isModified: boolean
      ): string => {
        const numVal = parseFloat(val.toString());
        if (isNaN(numVal)) return val.toString();

        if (
          paramName.includes("wave") ||
          paramName.includes("swell") ||
          paramName.includes("height")
        ) {
          return numVal.toFixed(1);
        }

        // Wind speed parameters - show decimals only if modified
        if (paramName.includes("wind") && paramName.includes("speed")) {
          if (isModified) {
            return val.toString();
          } else {
            return Math.round(numVal).toString();
          }
        }

        return val.toString();
      };

      let originalLimitValue = originalField?.value;
      if (
        typeof originalLimitValue === "string" &&
        (originalLimitValue.startsWith("OP_AND:") ||
          originalLimitValue.startsWith("OP_OR:"))
      ) {
        originalLimitValue = originalLimitValue.replace(/^OP_(AND|OR):/, "");
      }
      const isLimitModified = String(originalLimitValue) !== String(cleanValue);

      const isMarginModified =
        String(originalField?.margin_value) !== String(field.margin_value);
      const formattedLimitValue = formatValue(
        cleanValue,
        field.field_name,
        isLimitModified
      );
      const formattedMarginValue = formatValue(
        field.margin_value,
        field.field_name,
        isMarginModified
      );

      const comparator = getComparisonSymbol(field.comparison_operator_id);

const fieldText = `${displayName}${unit} Limit: ${comparator}${formattedLimitValue} Margin: ${comparator}${formattedMarginValue}`;

      return fieldText;
    });

    let logicalOperator = "AND";

    const fieldWithLogicalOp = sortedFields.find((f) => f.logical_operator);
    if (fieldWithLogicalOp) {
      logicalOperator = fieldWithLogicalOp.logical_operator!;
    } else {
      if (sortedFields.length > 0) {
        const firstField = sortedFields[0];
        if (firstField.comparison_operator_id === 1) {
          logicalOperator = "AND";
        } else if (firstField.comparison_operator_id === 2) {
          logicalOperator = "OR";
        }
      }

      const fieldWithOpInValue = sortedFields.find(
        (f) =>
          typeof f.value === "string" &&
          (f.value.startsWith("OP_AND:") || f.value.startsWith("OP_OR:"))
      );
      if (fieldWithOpInValue && typeof fieldWithOpInValue.value === "string") {
        logicalOperator = fieldWithOpInValue.value.startsWith("OP_OR:")
          ? "OR"
          : "AND";
      }
    }

    return fieldDescriptions
      .map((desc, index) => {
        if (index < fieldDescriptions.length - 1) {
          return `${desc} ${logicalOperator}`;
        }
        return desc;
      })
      .join("\n");
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = tableScrollRef.current;
      if (!scrollContainer) return;

      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
      const maxScroll = scrollWidth - clientWidth;

      setShowScrollIndicators({
        left: scrollLeft > 10,
        right: scrollLeft < maxScroll - 10 && maxScroll > 0,
      });
    };

    const scrollContainer = tableScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      handleScroll();

      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [showParameterSettings]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    const settingsContainer = e.currentTarget.closest(
      ".inline-parameter-settings"
    ) as HTMLElement;
    if (settingsContainer) {
      const rect = settingsContainer.getBoundingClientRect();

      if (settingsPosition.x === 0 && settingsPosition.y === 0) {
        setSettingsPosition({
          x: rect.left,
          y: rect.top,
        });
      }

      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });

      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent): void => {
    if (isDragging) {
      e.preventDefault();
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      setSettingsPosition({
        x: newX,
        y: newY,
      });
    }
  };

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      const handleMove = (e: MouseEvent) => handleMouseMove(e);
      const handleUp = () => handleMouseUp();

      document.addEventListener("mousemove", handleMove, { passive: false });
      document.addEventListener("mouseup", handleUp);
      document.addEventListener("mouseleave", handleUp);

      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";

      return () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.removeEventListener("mouseleave", handleUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragOffset.x, dragOffset.y]);

  const handleOpenModal = (): void => {
    setShowParameterSettings(true);
  };

  const handleCloseModal = (): void => {
    setShowParameterSettings(false);
    setSettingsPosition({ x: 0, y: 0 });
    setIsDragging(false);
  };

  useEffect(() => {
    store.dispatch({
      type: "TOGGLE_MENU",
      payload: windowWidth.current > 1000 ? true : false,
    });
  }, []);

  useEffect(() => {
    setOpen(data.toggle);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
           const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_IP}/api/weather/`,
          { forecast_id: localStorage.getItem("fid") },
          {
            headers: {
              "Content-Type": "application/json; charset=UTF-8",
              Authorization: "Basic " + btoa("admin:admin"),
            },
          }
        );
        const data = response.data;
// Transform the data to match CriteriaDetail type

const transformedDetails = (data.criteria_detail_datas || []).map((detail: any) => {
  let logicalOp: string | undefined;
  if (detail.logical_operator_id === 1) {
    logicalOp = 'AND';
  } else if (detail.logical_operator_id === 2) {
    logicalOp = 'OR';
  }

  return {
    forecast_osf_criteria_id: detail.forecast_osf_criteria_id,
    field_id: detail.field_id,
    field_name: detail.field_name,
    margin_value: detail.margin_value,
    value: detail.value,
    comparison_operator_id: detail.comparison_operator_id ?? 2, // ← use actual value from API, fallback to 2 (strict >)
    logical_operator: logicalOp,
    parameter_display_name: detail.parameter_display_name
  };
});
setCriteriaDatas(data.criteria_datas || []);
setCriteriaDetailDatas(transformedDetails);
setOriginalCriteriaDetailDatas(transformedDetails);
setTableDatas(data.datas || []);
        setSelectValue(
          String(data.criteria_detail_datas[0]?.forecast_osf_criteria_id || "")
        );

        setLoading(false);
        setDataUpdated(false);
      } catch (error) {
        console.error("Error fetching data from API:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [dataUpdated]);

  const handleParameterChange = (
    updatedCriteriaDetails: CriteriaDetail[]
  ): void => {
    setCriteriaDetailDatas(updatedCriteriaDetails);
  };

  const handleReset = () => {
    setCriteriaDetailDatas([...originalCriteriaDetailDatas]);
  };


function parseUTCDate(datetimeutc: string): Date {
  // Input format: "MM/DD/YYYY HH:MM"
  const [datePart, timePart] = datetimeutc.split(" ");
  const [month, day, year] = datePart.split("/");
  const [hours, minutes] = (timePart || "00:00").split(":");
  // Construct as UTC so there's no timezone shift
  return new Date(
    Date.UTC(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes)
    )
  );
}
function dateFormat(dateStr: string): string {
  const montharray = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const date = parseUTCDate(dateStr);            // ← was: new Date(datas[0].datetimeutc)
  return date.getUTCDate() + " " + montharray[date.getUTCMonth()];
}

  const hasCustomLimits = (criteriaId: number): boolean => {
    const currentCriteria = criteriaDetailDatas.filter(
      (c_data) => c_data.forecast_osf_criteria_id === criteriaId
    );
    const originalCriteria = originalCriteriaDetailDatas.filter(
      (c_data) => c_data.forecast_osf_criteria_id === criteriaId
    );

    if (currentCriteria.length !== originalCriteria.length) {
      return true;
    }

    return currentCriteria.some((current) => {
      const original = originalCriteria.find(
        (orig) => orig.field_id === current.field_id
      );
      if (!original) return true;

      const currentValue =
        typeof current.value === "string" &&
        (current.value.startsWith("OP_AND:") ||
          current.value.startsWith("OP_OR:"))
          ? current.value.replace(/^OP_(AND|OR):/, "")
          : current.value;

      const originalValue =
        typeof original.value === "string" &&
        (original.value.startsWith("OP_AND:") ||
          original.value.startsWith("OP_OR:"))
          ? original.value.replace(/^OP_(AND|OR):/, "")
          : original.value;

      return (
        currentValue !== originalValue ||
        current.margin_value !== original.margin_value ||
        current.comparison_operator_id !== original.comparison_operator_id ||
        current.logical_operator !== original.logical_operator
      );
    });
  };

  function getColortwo(
    criteriaDetailDatas: CriteriaDetail[],
    data: Record<string, number | null | undefined>,
    SelectValue: string
  ): string {
    const selectedCriteria = criteriaDetailDatas.filter(
      (c_data) => c_data.forecast_osf_criteria_id === parseInt(SelectValue, 10)
    );

    if (selectedCriteria.length === 0) {
      return "white";
    }

    let logicalOperator = "AND";

    const criteriaWithOperator = selectedCriteria.find(
      (c) => c.logical_operator
    );
    if (criteriaWithOperator && criteriaWithOperator.logical_operator) {
      logicalOperator = criteriaWithOperator.logical_operator;
    } else {
      const criteriaWithOpInValue = selectedCriteria.find(
        (c) =>
          typeof c.value === "string" &&
          (c.value.startsWith("OP_AND:") || c.value.startsWith("OP_OR:"))
      );
      if (
        criteriaWithOpInValue &&
        typeof criteriaWithOpInValue.value === "string"
      ) {
        logicalOperator = criteriaWithOpInValue.value.startsWith("OP_OR:")
          ? "OR"
          : "AND";
      }
    }

    const colorResults = selectedCriteria.map((c_data) => {
      const fieldValue = data[c_data.field_name];

      let cleanValue = c_data.value;
      if (
        typeof cleanValue === "string" &&
        (cleanValue.startsWith("OP_AND:") || cleanValue.startsWith("OP_OR:"))
      ) {
        cleanValue = cleanValue.replace(/^OP_(AND|OR):/, "");
      }

      return getColorForFieldtwo(
        fieldValue,
        parseFloat(c_data.margin_value),
        parseFloat(cleanValue),
        c_data.comparison_operator_id
      );
    });

    if (logicalOperator === "OR") {
      if (colorResults.includes("green_overview")) {
        return "green_overview";
      } else if (colorResults.includes("yellow_overview")) {
        return "yellow_overview";
      } else {
        return "red_overview";
      }
    } else {
      if (colorResults.includes("red_overview")) {
        return "red_overview";
      } else if (colorResults.includes("yellow_overview")) {
        return "yellow_overview";
      } else {
        return "green_overview";
      }
    }
  }

  function getColorForFieldtwo(
    data: number | null | undefined,
    marginValue: number,
    limitValue: number,
    comparisonOperatorId: number
  ): string {
    if (data === undefined || data === null) {
      return "white";
    }

    const isInMarginalZone =
      (limitValue < data && data <= marginValue) ||
      (marginValue <= data && data < limitValue);

    if (isInMarginalZone) {
      return "yellow_overview";
    }

    switch (comparisonOperatorId) {
      case 1:
        return data < limitValue ? "red_overview" : "green_overview";

      case 2:
        return data <= limitValue ? "green_overview" : "red_overview";

      case 3:
        return data >= limitValue ? "green_overview" : "red_overview";

      case 4:
        return data <= limitValue ? "green_overview" : "red_overview";

      case 5:
        return data === limitValue ? "green_overview" : "red_overview";

      case 6:
        return data !== limitValue ? "green_overview" : "red_overview";

      case 7:
        return data >= marginValue && data <= limitValue
          ? "green_overview"
          : "red_overview";

      case 8:
        return data === null ? "green_overview" : "red_overview";

      case 9:
        return data !== null ? "green_overview" : "red_overview";

      default:
        return "white";
    }
  }

  function getColor(
  criteriaDetailDatas: CriteriaDetail[],
  data: Record<string, number | null | undefined>,
  SelectValue: string
): string {
  const criteriaId = parseInt(SelectValue, 10);

  // Always use the current criteria details (which may be modified)
  const selectedCriteria = criteriaDetailDatas.filter(
    (c_data) => c_data.forecast_osf_criteria_id === criteriaId
  );

  if (selectedCriteria.length === 0) {
    return "white";
  }

  // Determine the logical operator for this criteria
  let logicalOperator = "AND";
  
  const criteriaWithOperator = selectedCriteria.find(
    (c) => c.logical_operator
  );
  if (criteriaWithOperator && criteriaWithOperator.logical_operator) {
    logicalOperator = criteriaWithOperator.logical_operator;
  } else {
    const criteriaWithOpInValue = selectedCriteria.find(
      (c) =>
        typeof c.value === "string" &&
        (c.value.startsWith("OP_AND:") || c.value.startsWith("OP_OR:"))
    );
    if (
      criteriaWithOpInValue &&
      typeof criteriaWithOpInValue.value === "string"
    ) {
      logicalOperator = criteriaWithOpInValue.value.startsWith("OP_OR:")
        ? "OR"
        : "AND";
    }
  }

  const colorResults = selectedCriteria.map((c_data) => {
    const fieldValue = data[c_data.field_name];

    let cleanValue = c_data.value;
    if (
      typeof cleanValue === "string" &&
      (cleanValue.startsWith("OP_AND:") || cleanValue.startsWith("OP_OR:"))
    ) {
      cleanValue = cleanValue.replace(/^OP_(AND|OR):/, "");
    }

    return getColorForField(
      fieldValue,
      parseFloat(c_data.margin_value),
      parseFloat(cleanValue),
      c_data.comparison_operator_id
    );
  });

  // Apply logical operator logic
  if (logicalOperator === "OR") {
    // OR logic: Show the worst case (prioritize red > yellow > green)
    if (colorResults.includes("red_overview")) {
      return "red_overview";
    } else if (colorResults.includes("yellow_overview")) {
      return "yellow_overview";
    } else {
      return "green_overview";
    }
  } else {
    // AND logic: Show red if ANY is red, yellow if ANY is yellow (and none are red), otherwise green
    if (colorResults.includes("red_overview")) {
      return "red_overview";
    } else if (colorResults.includes("yellow_overview")) {
      return "yellow_overview";
    } else if (colorResults.every((color) => color === "green_overview")) {
      return "green_overview";
    }
  }

  return "white";
}
function getColorForField(
  data: number | null | undefined,
  marginValue: number,
  limitValue: number,
  comparisonOperatorId: number
): string {
  if (data === undefined || data === null) {
    return "white";
  }

  // For weather criteria: >= operator (comparisonOperatorId = 4)
  // Red: value >= limit (exceeds limit - dangerous)
  // Yellow: marginal <= value < limit (in marginal zone)
  // Green: value < marginal (safe)
  if (comparisonOperatorId === 4) {
    if (data >= limitValue) {
      return "red_overview"; // Exceeds limit
    } else if (data >= marginValue && data < limitValue) {
      return "yellow_overview"; // In marginal zone
    } else {
      return "green_overview"; // Safe
    }
  }

  // For <= operator (comparisonOperatorId = 3)
  if (comparisonOperatorId === 3) {
    if (data <= limitValue) {
      return "red_overview";
    } else if (data > limitValue && data <= marginValue) {
      return "yellow_overview";
    } else {
      return "green_overview";
    }
  }

  // Legacy support for other operators
  switch (comparisonOperatorId) {
    case 1: // 
      return data < limitValue
        ? "red_overview"
        : data < marginValue
        ? "yellow_overview"
        : "green_overview";
    case 2: // >
      return data > limitValue
        ? "red_overview"
        : data > marginValue
        ? "yellow_overview"
        : "green_overview";
    default:
      return "white";
  }
}

  return (
    <div
      className={`weather-sidebar-container ${
        open ? "sideNavOpen" : "sideNavClose"
      }`}
    >
      <Box className="bg-default sidenav-full">
        <div className="content-wrap dashboard">
          <div className="weather-component">
            {!loading ? (
              criteriaDatas?.length > 0 ? (
                <Main
                  open={open}
                  className={"main"}
                  style={{
                    display: "flex",
                    paddingBlock: "10px",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: "35px",
                  }}
                >
                  <div className={"maincontainer"}>
                    {TableDatas.map((datas: any) => (
                   
<div key={datas[0].datetimeutc} className={"mini_header_box"}>
  <div className={"mini_header_main"}>
    <span className={"mini_header_date"}>
      {dateFormat(datas[0].datetimeutc)}   {/* pass string, not Date object */}
    </span>
  </div>
  <div className={"mini_header_time"}>
    {datas.map((data: any) => (
      <span key={data.datetimeutc} className={"mini_header_time"}>
        {parseUTCDate(data.datetimeutc).getUTCHours() === 0
          ? "0"
          : parseUTCDate(data.datetimeutc).getUTCHours()}
      </span>
    ))}
  </div>
                        {criteriaDatas?.map((rows: any) => (
                          <div
                            key={rows.criteria_name}
                          >
                            <div className="criteria-header">
                              <span>
                                {rows.criteria_name}
                                {hasCustomLimits(
                                  rows.forecast_osf_criteria_id
                                ) && (
                                  <span style={{ color: "red" }}>
                                    {" "}
                                    (Default limit adjusted)
                                  </span>
                                )}
                              </span>
                              <div
                                className="criteria-description"
                              >
                                {generateCriteriaDescription(
                                  rows.forecast_osf_criteria_id
                                )
                                  .split("\n")
                                  .map((line, index) => (
                                    <div
                                      key={index}
                                      style={{ marginBottom: "2px" }}
                                    >
                                      {line}
                                    </div>
                                  ))}
                              </div>
                            </div>
                            <div className={"mini_color_box"}>
                              {datas.map((data: any, index: number) => (
                                <span
                                  key={`${data.datetimeutc}-${index}`}
                                  style={{ width: "100%" }}
                                  className={getColor(
                                    criteriaDetailDatas,
                                    data,
                                    rows.forecast_osf_criteria_id.toString()
                                  )}
                                ></span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {!showParameterSettings && (
                    <div className={"weather-modify-button"}>
                      <button
                        onClick={handleOpenModal}
                        className={"modify-button"}
                      >
                        Set custom limits
                      </button>
                    </div>
                  )}

                  {showParameterSettings && (
                    <div
                      className="inline-parameter-settings"
                      style={{
                        position:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? "fixed"
                            : "relative",
                        top:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? `${settingsPosition.y}px`
                            : "auto",
                        left:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? `${settingsPosition.x}px`
                            : "auto",
                        zIndex:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? 9999
                            : "auto",
                        width:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? "100%"
                            : "100%",
                        maxWidth:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? "1000px"
                            : "1000px",
                        marginBottom:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? "0"
                            : "30px",
                        margin:
                          settingsPosition.x !== 0 || settingsPosition.y !== 0
                            ? "0"
                            : "0 auto 30px auto",
                      }}
                    >
                      <div className={"parameter-settings-header"}>
                        <div
                          className="drag-indicator"
                          onMouseDown={handleMouseDown}
                          style={{
                            cursor: isDragging ? "grabbing" : "grab",
                            userSelect: "none",
                          }}
                        >
                          Click to drag
                        </div>

                        <button
                          onClick={handleCloseModal}
                          className={"close-settings-btn"}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          ×
                        </button>
                      </div>
                      <WeatherParameterSettings
                        criteriaDatas={criteriaDatas}
                        criteriaDetailDatas={criteriaDetailDatas}
                        originalCriteriaDetailDatas={
                          originalCriteriaDetailDatas
                        }
                        onParameterChange={handleParameterChange}
                        onReset={handleReset}
                        tableScrollRef={tableScrollRef}
                        showScrollIndicators={showScrollIndicators}
                      />
                    </div>
                  )}

                  <div className={"legend_div"}>
                    <span className={"legend_heading"}>Status</span>
                    <div className={"lengend_details_div"}>
                      <div className={"legend_inner_div"}>
                        <span
                          style={{ width: "2.5em" }}
                          className={["high_legend"].join(" ")}
                        ></span>
                        <span className={"legend_content"}>
                          Above the limit
                        </span>
                      </div>
                      <div className={"legend_inner_div"}>
                        <span
                          style={{ width: "2.5em" }}
                          className={["normal_legend"].join(" ")}
                        ></span>
                        <span className={"legend_content"}>Marginal</span>
                      </div>
                      <div className={"legend_inner_div"}>
                        <span
                          style={{ width: "2.5em" }}
                          className={["low_legend"].join(" ")}
                        ></span>
                        <span className={"legend_content"}>
                          Below the limit
                        </span>
                      </div>
                    </div>
                  </div>
                </Main>
              ) : loading === false ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "90vh",
                  }}
                >
                  <p className={"noData"}>Weather Windows is not included in your package</p>
                </div>
              ) : null
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "90vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WeatherLoader />
              </div>
            )}
          </div>
        </div>
      </Box>
    </div>
  );
}
